import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Download, Plus, Trash2, Wallet, ArrowDownRight, ArrowUpRight,
} from "lucide-react";
import api, { API, formatApiError } from "@/services/api";
import { fmtMoney, fmtDate } from "@/utils/format";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CAT_LABEL = {
  system_total: "Kapital",
  logistics: "Lojistik",
  agent_funding: "Bay ajan",
  agent_withdrawal: "Retrè",
  delivered: "Livre",
};

const CAT_TONE = {
  system_total: "text-brand-blue dark:text-blue-300",
  logistics: "text-rose-600 dark:text-rose-400",
  agent_funding: "text-amber-700 dark:text-amber-400",
  agent_withdrawal: "text-rose-600 dark:text-rose-400",
  delivered: "text-emerald-700 dark:text-emerald-400",
};

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function mondayOf(d = new Date()) {
  const x = new Date(d.getTime());
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  return ymdLocal(x);
}

async function downloadPdf(path, filename) {
  const token = localStorage.getItem("tgs-token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("PDF echwe");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function FlowRow({ label, value, tone = "neutral", hint, testId, strong }) {
  const tones = {
    neutral: "text-foreground",
    out: "text-rose-600 dark:text-rose-400",
    in: "text-emerald-700 dark:text-emerald-400",
    gold: "text-brand-gold",
    mute: "text-muted-foreground",
  };
  return (
    <div
      data-testid={testId}
      className={`flex items-baseline justify-between gap-6 py-3.5 ${strong ? "border-t border-brand-gold/30 pt-5 mt-1" : "border-b border-white/10 last:border-0"}`}
    >
      <div className="min-w-0">
        <div className={`text-[13px] tracking-wide ${strong ? "font-semibold text-white" : "text-slate-300"}`}>
          {label}
        </div>
        {hint ? <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{hint}</div> : null}
      </div>
      <div
        className={`shrink-0 font-heading tabular-nums tracking-tight ${
          strong ? "text-2xl sm:text-3xl font-bold text-brand-gold" : `text-base sm:text-lg font-semibold ${tones[tone]}`
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default function ControlBoardPage() {
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [category, setCategory] = useState("system_total");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [activityFilter, setActivityFilter] = useState("all"); // all | week | month
  const [monthKey, setMonthKey] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  const { data: globalSum, isLoading: loadingGlobal } = useQuery({
    queryKey: ["control-summary"],
    queryFn: async () => (await api.get("/control-board/summary")).data,
  });

  const { data: weeks = [] } = useQuery({
    queryKey: ["control-weeks"],
    queryFn: async () => (await api.get("/control-board/weeks")).data,
  });

  const { data: months = [] } = useQuery({
    queryKey: ["control-months"],
    queryFn: async () => (await api.get("/control-board/months")).data,
  });

  const { data: week, isLoading: loadingWeek } = useQuery({
    queryKey: ["control-week", weekStart],
    queryFn: async () => (await api.get("/control-board/week", { params: { week_start: weekStart } })).data,
    enabled: activityFilter === "week",
  });

  const monthParts = useMemo(() => {
    const [y, m] = (monthKey || "").split("-").map(Number);
    return { year: y || new Date().getFullYear(), month: m || (new Date().getMonth() + 1) };
  }, [monthKey]);

  const { data: monthData, isLoading: loadingMonth } = useQuery({
    queryKey: ["control-month", monthParts.year, monthParts.month],
    queryFn: async () =>
      (await api.get("/control-board/month", {
        params: { year: monthParts.year, month: monthParts.month },
      })).data,
    enabled: activityFilter === "month",
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["control-summary"] });
    qc.invalidateQueries({ queryKey: ["control-week"] });
    qc.invalidateQueries({ queryKey: ["control-weeks"] });
    qc.invalidateQueries({ queryKey: ["control-month"] });
    qc.invalidateQueries({ queryKey: ["control-months"] });
  };

  const addMut = useSingleFlightMutation({
    mutationFn: async () =>
      (await api.post("/control-board/entries", {
        week_start: weekStart,
        category,
        amount: Number(amount),
        note,
      })).data,
    onSuccess: () => {
      toast.success("Antre ajoute");
      setAmount("");
      setNote("");
      invalidateAll();
    },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/control-board/entries/${id}`)).data,
    onSuccess: () => {
      toast.success("Antre efase");
      invalidateAll();
    },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail)),
  });

  const weekOptions = useMemo(() => {
    const map = new Map();
    for (const w of weeks) map.set(w.week_start, w);
    if (!map.has(weekStart)) {
      map.set(weekStart, { week_start: weekStart, week_label: weekStart });
    }
    return [...map.values()].sort((a, b) => (b.week_start || "").localeCompare(a.week_start || ""));
  }, [weeks, weekStart]);

  const monthOptions = useMemo(() => {
    const map = new Map();
    for (const m of months) map.set(m.month_key, m);
    if (!map.has(monthKey)) {
      map.set(monthKey, {
        month_key: monthKey,
        month_label: monthKey,
        year: monthParts.year,
        month: monthParts.month,
      });
    }
    return [...map.values()].sort((a, b) => (b.month_key || "").localeCompare(a.month_key || ""));
  }, [months, monthKey, monthParts]);

  const g = globalSum || {};
  const loading =
    loadingGlobal
    || (activityFilter === "week" && loadingWeek)
    || (activityFilter === "month" && loadingMonth);
  const entries =
    activityFilter === "week"
      ? (week?.activity || [])
      : activityFilter === "month"
        ? (monthData?.activity || [])
        : (g.activity || g.entries || []);

  return (
    <div className="relative w-full max-w-5xl lg:mx-auto space-y-8 sm:space-y-10 pb-8 overflow-x-hidden">
      {/* Soft brand atmosphere — clipped so mobile stays full-bleed */}
      <div className="pointer-events-none absolute -top-8 right-0 w-[min(420px,90vw)] h-[min(420px,90vw)] rounded-full bg-brand-blue/10 dark:bg-brand-blue/20 blur-3xl" />
      <div className="pointer-events-none absolute top-40 left-0 w-[min(280px,70vw)] h-[min(280px,70vw)] rounded-full bg-brand-gold/10 blur-3xl" />

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-brand-gold font-semibold">
            Takara Global Services
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-foreground">
            Tablo Kontwòl
          </h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground leading-relaxed">
            Kontablite finance globale — caisse, ajan, ak livrezon nan yon sèl gade.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 border-brand-gold/40 text-foreground hover:bg-brand-gold/10 hover:border-brand-gold/60"
            onClick={async () => {
              try {
                await downloadPdf("/control-board/summary/pdf", "tgs-kontwol-depi-kreyasyon.pdf");
                toast.success("PDF globale telechaje");
              } catch {
                toast.error("Pa ka telechaje PDF");
              }
            }}
          >
            <Download className="h-4 w-4" /> PDF globale
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={async () => {
              try {
                await downloadPdf(
                  `/control-board/week/pdf?week_start=${encodeURIComponent(weekStart)}`,
                  `tgs-kontwol-${weekStart}.pdf`,
                );
                toast.success("PDF semèn telechaje");
              } catch {
                toast.error("Pa ka telechaje PDF");
              }
            }}
          >
            <Download className="h-4 w-4" /> PDF semèn
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="control-pdf-month"
            onClick={async () => {
              try {
                await downloadPdf(
                  `/control-board/month/pdf?year=${monthParts.year}&month=${monthParts.month}`,
                  `tgs-kontwol-${monthKey}.pdf`,
                );
                toast.success("PDF mwa telechaje");
              } catch {
                toast.error("Pa ka telechaje PDF");
              }
            }}
          >
            <Download className="h-4 w-4" /> PDF mwa
          </Button>
        </div>
      </motion.header>

      {/* Hero composition: Caisse + flow */}
      {loadingGlobal ? (
        <Skeleton className="h-[420px] w-full rounded-3xl" />
      ) : (
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="relative overflow-hidden rounded-3xl bg-[#0B1B3A] text-white shadow-2xl shadow-brand-blue/20"
        >
          <div className="absolute inset-0 opacity-[0.35] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 80% 60% at 100% 0%, rgba(212,160,40,0.35), transparent 55%), radial-gradient(ellipse 60% 50% at 0% 100%, rgba(37,99,235,0.35), transparent 50%)",
            }}
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent" />

          <div className="relative grid lg:grid-cols-[1.05fr_0.95fr] gap-0">
            {/* Dominant figure */}
            <div className="p-5 sm:p-8 lg:p-12 flex flex-col justify-between min-h-[280px] sm:min-h-[320px]">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-gold/90 font-semibold">
                  <Wallet className="h-3.5 w-3.5" />
                  Caisse Admin
                </div>
                <div
                  data-testid="ctrl-caisse"
                  className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight tabular-nums text-white"
                >
                  {fmtMoney(g.caisse_admin ?? 0)}
                </div>
                <p className="mt-4 text-sm text-slate-300 max-w-sm leading-relaxed">
                  Kob ki rete nan men administrasyon an apre bay ajan, depans, ak retrè peye.
                </p>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Lajan ki rete total</div>
                  <div data-testid="ctrl-rete-total" className="mt-2 font-heading text-2xl font-semibold tabular-nums text-brand-gold">
                    {fmtMoney(g.lajan_rete_total ?? 0)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">Caisse + nan men ajan</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Frè kolekte</div>
                  <div className="mt-2 font-heading text-2xl font-semibold tabular-nums">
                    {fmtMoney(g.fees_collected ?? 0)}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">{g.delivered_count ?? 0} transfè livre</div>
                </div>
              </div>
            </div>

            {/* Calculation narrative */}
            <div className="border-t lg:border-t-0 lg:border-l border-white/10 bg-black/20 backdrop-blur-sm p-5 sm:p-8 lg:p-10">
              <div className="text-[10px] uppercase tracking-[0.24em] text-brand-gold/80 font-semibold mb-2">
                Kalkil globale
              </div>
              <FlowRow
                label="Lajan total sistèm"
                value={fmtMoney(g.lajan_total_sistem ?? 0)}
                tone="neutral"
                testId="ctrl-total"
              />
              <FlowRow
                label="− Kob bay ajan"
                value={fmtMoney(g.kob_bay_ajan ?? 0)}
                tone="out"
                hint={`Livre ${fmtMoney(g.ajan_livre ?? 0)} · Rete nan men ${fmtMoney(g.rete_nan_men_ajan ?? 0)}`}
                testId="ctrl-funded"
              />
              <FlowRow
                label="− Depans lojistik"
                value={fmtMoney(g.logistics ?? 0)}
                tone="out"
              />
              <FlowRow
                label="− Retrè peye"
                value={fmtMoney(g.agent_withdrawal ?? 0)}
                tone="out"
              />
              <FlowRow
                label="= Caisse Admin"
                value={fmtMoney(g.caisse_admin ?? 0)}
                tone="gold"
                strong
                testId="ctrl-caisse-flow"
              />
            </div>
          </div>
        </motion.section>
      )}

      {/* Agent cash story — one purpose */}
      {!loadingGlobal && (
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.16 }}
          className="grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-border bg-border"
        >
          {[
            {
              id: "ctrl-livre",
              label: "Livre",
              value: fmtMoney(g.ajan_livre ?? 0),
              sub: `${g.delivered_count ?? 0} transfè konplete`,
              icon: ArrowUpRight,
            },
            {
              id: "ctrl-rete-ajan",
              label: "Rete nan men ajan",
              value: fmtMoney(g.rete_nan_men_ajan ?? 0),
              sub: "Bay ajan − livre",
              icon: Wallet,
            },
            {
              id: "ctrl-bay",
              label: "Kob bay ajan",
              value: fmtMoney(g.kob_bay_ajan ?? 0),
              sub: "Total finansman aktif",
              icon: ArrowDownRight,
            },
          ].map((m) => (
            <div key={m.id} data-testid={m.id} className="bg-card px-4 py-5 sm:px-6 sm:py-7">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                  {m.label}
                </span>
                <m.icon className="h-4 w-4 text-brand-gold/80" />
              </div>
              <div className="mt-3 font-heading text-2xl sm:text-3xl font-bold tabular-nums tracking-tight">
                {m.value}
              </div>
              <div className="mt-1.5 text-xs text-muted-foreground">{m.sub}</div>
            </div>
          ))}
        </motion.section>
      )}

      {/* Entry form */}
      <section className="relative rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Nouvo antre</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Kapital oswa lojistik. Bay ajan, retrè, ak livre ajoute otomatikman.
            </p>
          </div>
        </div>
        <form
          className="grid sm:grid-cols-2 lg:grid-cols-[1.1fr_1fr_1.3fr_auto] gap-4 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (addMut.isPending) return;
            if (!amount || Number(amount) <= 0) {
              toast.error("Antre yon montan valid");
              return;
            }
            addMut.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Kalite</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="control-category" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system_total">Lajan total sistèm</SelectItem>
                <SelectItem value="logistics">Depans lojistik</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Montan USD</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="control-amount"
              className="h-11 font-heading tabular-nums text-base"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Nòt</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Opsyonèl"
              data-testid="control-note"
              className="h-11"
            />
          </div>
          <Button
            type="submit"
            disabled={addMut.isPending}
            data-testid="control-add-btn"
            className="h-11 gap-2 bg-brand-blue hover:bg-brand-blueHover text-white px-6"
          >
            <Plus className="h-4 w-4" /> Anrejistre
          </Button>
        </form>
      </section>

      {/* Activity ledger */}
      <section className="relative">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Aktivite finance</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Tout mouvman — kapital, ajan, livre, retrè.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Lis</Label>
              <Select value={activityFilter} onValueChange={setActivityFilter}>
                <SelectTrigger className="w-[160px] h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout (globale)</SelectItem>
                  <SelectItem value="week">Yon semèn</SelectItem>
                  <SelectItem value="month">Yon mwa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {activityFilter === "week" && (
              <>
                <div className="space-y-1 min-w-[180px]">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Semèn</Label>
                  <Select value={weekStart} onValueChange={setWeekStart}>
                    <SelectTrigger data-testid="control-week-select" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekOptions.map((w) => (
                        <SelectItem key={w.week_start} value={w.week_start}>
                          {w.week_label || w.week_start}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1.5 text-muted-foreground"
                  onClick={async () => {
                    try {
                      await downloadPdf(
                        `/control-board/week/pdf?week_start=${encodeURIComponent(weekStart)}`,
                        `tgs-kontwol-${weekStart}.pdf`,
                      );
                      toast.success("PDF semèn telechaje");
                    } catch {
                      toast.error("Pa ka telechaje PDF");
                    }
                  }}
                >
                  <Download className="h-3.5 w-3.5" /> PDF
                </Button>
              </>
            )}
            {activityFilter === "month" && (
              <>
                <div className="space-y-1 min-w-[180px]">
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Mwa</Label>
                  <Select value={monthKey} onValueChange={setMonthKey}>
                    <SelectTrigger data-testid="control-month-select" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((m) => (
                        <SelectItem key={m.month_key} value={m.month_key}>
                          {m.month_label || m.month_key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1.5 text-muted-foreground"
                  onClick={async () => {
                    try {
                      await downloadPdf(
                        `/control-board/month/pdf?year=${monthParts.year}&month=${monthParts.month}`,
                        `tgs-kontwol-${monthKey}.pdf`,
                      );
                      toast.success("PDF mwa telechaje");
                    } catch {
                      toast.error("Pa ka telechaje PDF");
                    }
                  }}
                >
                  <Download className="h-3.5 w-3.5" /> PDF
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border overflow-hidden bg-card">
          <div className="hidden sm:grid grid-cols-[7rem_7.5rem_1fr_6.5rem_2.5rem] gap-3 px-5 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">
            <div>Dat</div>
            <div>Kalite</div>
            <div>Detay</div>
            <div className="text-right">Montan</div>
            <div />
          </div>

          {loading && (
            <div className="p-5 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          )}

          {!loading && entries.length === 0 && (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Pa gen aktivite pou peryòd sa a.
            </div>
          )}

          <ul className="divide-y divide-border">
            {entries.map((e, idx) => {
              const isDelivered = e.category === "delivered" || e.source === "transfer";
              const isAuto = Boolean(e.funding_id || e.withdrawal_id || isDelivered);
              const tone = CAT_TONE[e.category] || "text-foreground";
              return (
                <motion.li
                  key={e.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(idx * 0.02, 0.3) }}
                  data-testid={`control-row-${e.id}`}
                  className="grid sm:grid-cols-[7rem_7.5rem_1fr_6.5rem_2.5rem] gap-2 sm:gap-3 px-5 py-4 items-center hover:bg-muted/30 transition-colors"
                >
                  <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {fmtDate(e.created_at)}
                  </div>
                  <div className={`text-sm font-medium ${tone}`}>
                    {CAT_LABEL[e.category] || e.category}
                    {isAuto ? (
                      <span className="ml-2 text-[9px] uppercase tracking-[0.14em] text-muted-foreground font-normal">
                        oto
                      </span>
                    ) : null}
                  </div>
                  <div className="text-sm text-muted-foreground truncate min-w-0 order-last sm:order-none col-span-full sm:col-span-1">
                    {e.note || "—"}
                  </div>
                  <div className="font-heading font-semibold tabular-nums text-sm text-right sm:text-right">
                    {fmtMoney(e.amount)}
                  </div>
                  <div className="flex justify-end">
                    {isAuto ? null : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive h-8 w-8"
                        onClick={() => delMut.mutate(e.id)}
                        data-testid={`control-del-${e.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ul>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground text-center sm:text-left">
          Kat kalkil yo toujou globale. Filtre a chanje lis aktivite a sèlman.
        </p>
      </section>
    </div>
  );
}
